import React from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { COURSE_UNITS_DATA } from '../data/unitsData'; 
import { FaCubes, FaArrowRight, FaArrowLeft, FaFolderOpen, FaLock } from 'react-icons/fa';

const UnitsPage = () => {
    const { navigateTo, currentPageData } = useNavigation();
    const { user } = useAuth();
    
    const courseId = currentPageData?.courseId;
    const courseTitle = currentPageData?.title || 'Select a Unit'; 
    const courseData = COURSE_UNITS_DATA[courseId];
    
    if (!courseId) {
         return (
            <div className="container mx-auto p-6 md:p-10 text-center">
                <h1 className="text-3xl font-bold text-red-500">Navigation Error</h1>
                <button onClick={() => navigateTo('eslCourses')} className="mt-6 bg-blue-600 text-white py-2 px-6 rounded-lg">
                    Go Back
                </button>
            </div>
        );
    }
    
    const units = courseData?.units || []; 

    const handleUnitSelect = (unit, index) => {
        // --- LOCK LOGIC FOR GUESTS ---
        // If user is not logged in AND they click anything other than the first unit
        if (!user && index > 0) {
            alert("This unit is locked for guests. Please Sign Up or Login to continue learning!");
            navigateTo('login');
            return;
        }

        if (unit.lessons && unit.lessons.length > 0) {
            const lessonFileName = unit.lessons[0];
            const lessonPath = lessonFileName.replace('.js', '');

            navigateTo(`lessons/${lessonPath}`, { 
                courseId, 
                unitId: unit.unitId,
                unitTitle: unit.title,
                courseTitle,
                timeLimit: unit.timeLimit || 60,
                lessonFileName: lessonFileName 
            });
        } else {
            alert("This unit has no lessons attached yet.");
        }
    };

    return (
        <div className="container mx-auto p-6 md:p-10">
            <div className="mb-6">
                <button 
                    onClick={() => navigateTo('eslCourses')} 
                    className="flex items-center text-gray-600 hover:text-blue-600 font-bold transition-colors"
                >
                    <FaArrowLeft className="mr-2" /> Back to Lessons
                </button>
            </div>

            <header className="mb-8 pb-4 border-b border-gray-200">
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-800">{courseTitle}</h1>
                <p className="text-xl text-gray-600 mt-2">Explore the units of this course.</p>
                {!user && (
                    <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400 text-blue-800 font-bold text-sm">
                        You are browsing as a guest. The first unit is unlocked for you!
                    </div>
                )}
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {units.length > 0 ? (
                    units.map((unit, index) => {
                        const isLocked = !user && index > 0;
                        return (
                            <div
                                key={unit.unitId}
                                className={`bg-white p-6 rounded-xl shadow-lg border-t-4 transition-all duration-300 ${
                                    isLocked 
                                    ? 'border-gray-300 opacity-70 grayscale-[0.5]' 
                                    : 'border-green-500 hover:shadow-xl hover:scale-[1.02] cursor-pointer'
                                }`}
                                onClick={() => handleUnitSelect(unit, index)}
                            >
                                <div className="flex justify-between items-start">
                                    <FaCubes className={`text-3xl ${isLocked ? 'text-gray-400' : 'text-green-500'} mb-4`} />
                                    {isLocked && <FaLock className="text-gray-400 text-xl" />}
                                </div>
                                <h2 className={`text-2xl font-bold ${isLocked ? 'text-gray-400' : 'text-gray-700'} mb-2`}>{unit.title}</h2>
                                <p className="text-gray-500 mb-4">{unit.description}</p>
                                
                                {isLocked ? (
                                    <p className="flex items-center text-gray-400 font-semibold italic">
                                        Locked for guests
                                    </p>
                                ) : (
                                    <p className="flex items-center text-green-600 font-semibold">
                                        Start Lesson <FaArrowRight className="ml-2" />
                                    </p>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="col-span-full text-center p-10 bg-white rounded-xl shadow-lg border-2 border-dashed border-red-300">
                        <FaFolderOpen className="text-6xl text-red-500 mx-auto mb-4" />
                        <p className="text-xl text-gray-600">No lessons defined for this course yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UnitsPage;